// The Effect edge of the receiver journey screens: the one module that runs the receiver calls.
// Each run hands React Query (or the screen) a Promise of the call's Result, so a typed failure is
// data the screen renders and a 4xx is never retried as if it were a transient throw; an aborted
// query interrupts its request.
import { queryOptions } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import { Effect, ManagedRuntime, Match, Result } from 'effect';
import { FetchHttpClient } from 'effect/http';
import type { HttpClient } from 'effect/http';
import type {
  OpinReceiverCapabilities,
  OpinReceiverConnection,
  OpinReceiverConnectionDetail,
  OpinReceiverConnectionPage,
  OpinReceiverTransmitterPage,
} from '@finnest/power-admin-contracts/opin-receiver';
import { resolvePowerAdminApiBaseUrl } from '../../runtime/power-admin-api.ts';
import {
  listConnections,
  readCapabilities,
  readConnection,
  receivedData,
  searchTransmitters,
} from './calls.ts';
import type {
  ConnectionFilters,
  ReceivedSelection,
  ReceivedPage,
  ReceiverCallFailure,
  ReceiverOperation,
  TransmitterSearch,
} from './calls.ts';

const receiverRuntime = ManagedRuntime.make(FetchHttpClient.layer);

/** What a call answered: its value or its typed failure. */
export type ReceiverOutcome<A> = Result.Result<A, ReceiverCallFailure>;

// The fetch is read per run, as the browser (or a test) has it now; the API base is resolved per
// run too.
const program = <A>(
  operation: ReceiverOperation<A>,
): Effect.Effect<ReceiverOutcome<A>, never, HttpClient.HttpClient> =>
  Effect.suspend(() => operation(resolvePowerAdminApiBaseUrl())).pipe(
    Effect.result,
    Effect.provideService(FetchHttpClient.Fetch, globalThis.fetch),
  );

const queried = <A>(
  operation: ReceiverOperation<A>,
  signal: AbortSignal,
): Promise<ReceiverOutcome<A>> => receiverRuntime.runPromise(program(operation), { signal });

/** Runs one operation against this deployment's Power API: a write, or a lookup by POST. */
export const runReceiverCall = <A>(operation: ReceiverOperation<A>): Promise<ReceiverOutcome<A>> =>
  receiverRuntime.runPromise(program(operation));

const KEY = ['power-admin', 'opin-receiver'] as const;

/** Every receiver query, to refresh them after a sync or a revocation. */
export const receiverQueryKey = KEY;

type ReceiverQueryOptions<A, Key extends readonly unknown[]> = ReturnType<
  typeof queryOptions<ReceiverOutcome<A>, Error, ReceiverOutcome<A>, Key>
>;

type TransmittersKey = readonly [...typeof KEY, 'transmitters', TransmitterSearch];

export const transmittersQueryOptions = (
  search: TransmitterSearch,
): ReceiverQueryOptions<OpinReceiverTransmitterPage, TransmittersKey> =>
  queryOptions({
    queryKey: [...KEY, 'transmitters', search] as const,
    queryFn: ({ signal }) => queried(searchTransmitters(search), signal),
    retry: false,
  });

type ConnectionsKey = readonly [...typeof KEY, 'connections', ConnectionFilters];

export const connectionsQueryOptions = (
  filters: ConnectionFilters,
): ReceiverQueryOptions<OpinReceiverConnectionPage, ConnectionsKey> =>
  queryOptions({
    queryKey: [...KEY, 'connections', filters] as const,
    queryFn: ({ signal }) => queried(listConnections(filters), signal),
    retry: false,
  });

type ConnectionCountKey = readonly [
  ...typeof KEY,
  'connection-count',
  OpinReceiverConnection['status'],
];

/** How many connections, of every application, are in `status`: the list's record count. */
export const connectionCountQueryOptions = (
  status: OpinReceiverConnection['status'],
): ReceiverQueryOptions<number, ConnectionCountKey> =>
  queryOptions({
    queryKey: [...KEY, 'connection-count', status] as const,
    queryFn: ({ signal }) =>
      queried(
        (apiBaseUrl) =>
          Effect.map(
            listConnections({ clientId: '', statuses: [status], search: '', page: 1 })(apiBaseUrl),
            (page) => page.meta.totalRecords,
          ),
        signal,
      ),
    retry: false,
    // A summary screen does not poll; the counts are read again whenever the operator comes back
    // to the tab, however recently they were read.
    refetchOnWindowFocus: 'always',
  });

// A connection waiting on the transmitter, syncing, or waiting for the lifecycle sweep to confirm
// its revocation changes on its own: the screen polls it.
const POLL_MS = 3000;
const MOVING: ReadonlySet<OpinReceiverConnection['status']> = new Set([
  'pending',
  'awaiting_auth',
  'syncing',
  'revocation_pending',
]);

/** Whether `connection` is still moving on its own. */
export const isMoving = (connection: OpinReceiverConnection): boolean =>
  MOVING.has(connection.status);

type ConnectionKey = readonly [...typeof KEY, 'connection', string];

const HTTP_SERVER_ERROR_FLOOR = 500;

// A refusal below 5xx is Power's definitive answer; no answer, a 5xx or a reply outside the
// contract may pass, so it never ends the polling of a connection still moving.
const definitive = (failure: ReceiverCallFailure): boolean =>
  Match.valueTags(failure, {
    ReceiverCallUnreachable: () => false,
    ReceiverReplyInvalid: () => false,
    ReceiverCallRefused: ({ status }) => status < HTTP_SERVER_ERROR_FLOOR,
  });

const pollWhileMoving = (
  query: Query<
    ReceiverOutcome<OpinReceiverConnectionDetail>,
    Error,
    ReceiverOutcome<OpinReceiverConnectionDetail>,
    ConnectionKey
  >,
): number | false => {
  const outcome = query.state.data;
  if (outcome === undefined) return false;
  return Result.match(outcome, {
    onSuccess: ({ connection }) => (isMoving(connection) ? POLL_MS : false),
    onFailure: (failure) => (definitive(failure) ? false : POLL_MS),
  });
};

export const connectionQueryOptions = (
  id: string,
): ReceiverQueryOptions<OpinReceiverConnectionDetail, ConnectionKey> =>
  queryOptions({
    queryKey: [...KEY, 'connection', id] as const,
    queryFn: ({ signal }) => queried(readConnection(id), signal),
    retry: false,
    refetchInterval: pollWhileMoving,
  });

type ReceivedKey = readonly [...typeof KEY, 'received', ReceivedSelection];

export const receivedDataQueryOptions = (
  selection: ReceivedSelection,
): ReceiverQueryOptions<ReceivedPage, ReceivedKey> =>
  queryOptions({
    queryKey: [...KEY, 'received', selection] as const,
    queryFn: ({ signal }) => queried(receivedData(selection), signal),
    retry: false,
  });

type CapabilitiesKey = readonly [...typeof KEY, 'capabilities'];

/** The revocation reasons the session's operator may choose; read once per session. */
export const capabilitiesQueryOptions = (): ReceiverQueryOptions<
  OpinReceiverCapabilities,
  CapabilitiesKey
> =>
  queryOptions({
    queryKey: [...KEY, 'capabilities'] as const,
    queryFn: ({ signal }) => queried(readCapabilities, signal),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
